import { useEffect, useRef, useState } from "react";
import { Loader2, MailCheck, ArrowLeft, Smartphone, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CODE_CLS = "w-full h-14 rounded-lg border border-slate-200 bg-white px-4 text-center font-display font-semibold focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";

export function OtpStep({ challenge, onBack, onDone, verify, resend: resendFn }) {
  const auth = useAuth();
  const verifyOtp = verify || auth.verifyOtp;
  const resendOtp = resendFn || auth.resendOtp;
  const [mode, setMode] = useState(challenge.method === "totp" ? "totp" : "email"); // totp | email | recovery
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ch, setCh] = useState(challenge);
  const [cooldown, setCooldown] = useState(challenge.method === "totp" ? 0 : 30);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [mode]);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const ready = mode === "recovery" ? code.replace(/[^a-z0-9]/gi, "").length >= 8 : code.length === 6;

  const submit = async (e) => {
    e?.preventDefault();
    if (!ready) return;
    setBusy(true);
    const res = mode === "recovery" ? await auth.verifyRecovery(ch.challengeId, code, remember) : await verifyOtp(ch.challengeId, code, remember);
    setBusy(false);
    if (res.ok) onDone(res);
    else { toast.error(res.error); setCode(""); inputRef.current?.focus(); }
  };

  const sendEmail = async () => {
    setBusy(true);
    const res = await resendOtp(ch.challengeId);
    setBusy(false);
    if (res.ok) {
      setCh({ ...ch, challengeId: res.challengeId, emailed: res.emailed, emailHint: res.emailHint || ch.emailHint, method: "email" });
      setMode("email"); setCode(""); setCooldown(30);
      toast.success(res.emailed ? "A code is on its way to your email" : "Code regenerated — email delivery failed, contact your admin");
    } else toast.error(res.error);
  };

  const banner = {
    totp: { Icon: Smartphone, title: "Open your authenticator app", text: <>Enter the 6-digit code shown for <b>Logerd</b> in Google Authenticator, Microsoft Authenticator or Authy.</> },
    email: { Icon: MailCheck, title: "Check your email", text: <>We sent a 6-digit code to <b data-testid="otp-email-hint">{ch.emailHint}</b>. It expires in 10 minutes.
      {!ch.emailed && <div className="mt-1 text-rose-600" data-testid="otp-email-failed">The email could not be sent — ask your admin to check the mail settings.</div>}</> },
    recovery: { Icon: LifeBuoy, title: "Use a recovery code", text: <>Enter one of the recovery codes you saved when you set up your authenticator app. Each code works once.</> },
  }[mode];

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="otp-form" data-mode={mode}>
      <div className="flex items-start gap-3 rounded-xl bg-sky-50 border border-sky-100 p-4">
        <banner.Icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
        <div className="text-sm text-slate-700">
          <div className="font-semibold text-slate-900" data-testid="otp-mode-title">{banner.title}</div>
          {banner.text}
        </div>
      </div>
      <label className="block">
        <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">{mode === "recovery" ? "Recovery code" : "Verification code"}</span>
        {mode === "recovery" ? (
          <input ref={inputRef} autoComplete="off" spellCheck={false} maxLength={20} value={code} placeholder="xxxx-xxxx-xxxx"
                 onChange={(e) => setCode(e.target.value.toLowerCase())} data-testid="otp-recovery-input"
                 className={`${CODE_CLS} text-xl tracking-[0.2em] font-mono`} />
        ) : (
          <input ref={inputRef} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} value={code}
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="otp-code-input"
                 className={`${CODE_CLS} text-3xl tracking-[0.5em]`} />
        )}
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="otp-remember-device" className="h-4 w-4 rounded border-slate-300" />
        Trust this device for 30 days
      </label>
      <button type="submit" disabled={busy || !ready} data-testid="otp-submit-btn"
              className="w-full h-12 rounded-lg bg-primary text-white font-display text-base font-semibold tracking-tight transition active:scale-[0.98] hover:shadow disabled:opacity-60 inline-flex items-center justify-center gap-2">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify & sign in
      </button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={onBack} data-testid="otp-back-btn" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900"><ArrowLeft className="h-3 w-3" /> Back</button>
        <div className="flex items-center gap-3">
          {mode === "email" && (
            <button type="button" onClick={sendEmail} disabled={cooldown > 0 || busy} data-testid="otp-resend-btn" className="font-medium text-primary hover:underline disabled:text-slate-400 disabled:no-underline">
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
          )}
          {mode !== "email" && challenge.method === "totp" && (
            <button type="button" onClick={sendEmail} disabled={busy} data-testid="otp-use-email-btn" className="font-medium text-primary hover:underline">Email me a code instead</button>
          )}
          {challenge.method === "totp" && mode !== "recovery" && (
            <button type="button" onClick={() => { setMode("recovery"); setCode(""); }} data-testid="otp-use-recovery-btn" className="font-medium text-slate-600 hover:underline">Use a recovery code</button>
          )}
          {mode === "recovery" && (
            <button type="button" onClick={() => { setMode("totp"); setCode(""); }} data-testid="otp-use-app-btn" className="font-medium text-slate-600 hover:underline">Use authenticator app</button>
          )}
        </div>
      </div>
    </form>
  );
}
